import {
  PdfGen,
  PdfUnit,
  PdfSize,
  PdfRect,
  PdfPoint,
  PdfBrush,
  PdfColor,
  PdfFont,
  ETextAlign,
} from '../../pdf/pdf';
import { ILayoutLabelGen } from '../layoutLabel';
import { OptsLabel } from '../optsLabel';
import { IItemCount } from '@educraft/base';
import { GUtil } from '../../pdf/gutil';
import { ESymbol } from '../../pdf/draw/symbol';
import { calcOrigin } from '../../core/genHelper';
import '../../pdf/draw/primitives';
import '../../pdf/draw/string';
import '../../pdf/draw/label';
import '../../pdf/draw/arrow';
import '../../pdf/draw/transforms';
import '../../pdf/draw/cut';

/**
 * Navigace (Navigation Exercise)
 *
 * Layout: 2×2 grid of cards per page (landscape not needed, uses portrait A4).
 * Each card: 6×6 navigation grid with start/end labels and 4 shuffled paths listed below.
 * One path is correct (reaches the end), three are distractors.
 *
 * Migrated from C# EduFrame.Img10.Generators.Navigace
 */

const NAV_DX = [0, 1, 0, -1];
const NAV_DY = [-1, 0, 1, 0];

interface NavCell {
  x: number;
  y: number;
}

function navCellEq(a: NavCell, b: NavCell): boolean {
  return a.x === b.x && a.y === b.y;
}

function navGenPath1(
  random: { next(min: number, max: number): number },
  length: number,
  sizeX: number,
  sizeY: number,
  start: NavCell
): { directions: number[]; end: NavCell } | null {
  const order = [0, 1, 2, 3];
  let c = { ...start };
  const directions: number[] = [];
  const visited: NavCell[] = [];

  for (let step = 0; step < length; step++) {
    for (let k = order.length - 1; k > 0; k--) {
      const j = random.next(0, k + 1);
      [order[k], order[j]] = [order[j], order[k]];
    }

    let moved = false;
    for (let j = 0; j < 4; j++) {
      const c1: NavCell = { x: c.x + NAV_DX[order[j]], y: c.y + NAV_DY[order[j]] };
      if (navCellEq(c1, start)) continue;
      if (visited.some((v) => navCellEq(v, c1))) continue;
      if (c1.x >= 0 && c1.y >= 0 && c1.x < sizeX && c1.y < sizeY) {
        c = c1;
        moved = true;
        directions.push(order[j]);
        visited.push({ ...c1 });
        break;
      }
    }

    if (!moved) return null;
  }

  return { directions, end: c };
}

function navGenPath(
  random: { next(min: number, max: number): number },
  length: number,
  sizeX: number,
  sizeY: number,
  start: NavCell
): { directions: number[]; end: NavCell } {
  while (true) {
    const result = navGenPath1(random, length, sizeX, sizeY, start);
    if (result) return result;
  }
}

export class Navigace implements ILayoutLabelGen, IItemCount {
  countDefault(): number {
    return 8;
  }
  countMin(): number {
    return 1;
  }
  countMax(): number {
    return 50;
  }

  public async generate(pdf: PdfGen, opts: OptsLabel): Promise<void> {
    const margin = PdfUnit.FromMillimeters(10);
    const countX = 2;
    const countY = 2;
    const cardsPerPage = countX * countY;
    const { origin, size } = calcOrigin(countX, countY, 0, PdfSize.A4);

    const sizeX = 6;
    const sizeY = 6;
    const length = 8;
    const listFont = new PdfFont(PdfFont.FONT_NAME, 14);
    const blueBrush = new PdfBrush(PdfColor.fromRgb(0, 0, 255));

    const count = opts.list.length;
    const pageCount = Math.ceil(count / cardsPerPage);
    const pages = Array.from({ length: pageCount }, () => pdf.addPage());

    let index = 0;

    for (let page = 0; page < pageCount; page++) {
      const pg = pages[page];

      for (let y = 0; y < countY; y++) {
        for (let x = 0; x < countX; x++) {
          if (index >= count) continue;

          const item = opts.list[index % opts.list.length];
          const {random} = opts.data;
          const start: NavCell = { x: random.next(0, sizeX), y: random.next(0, sizeY) };

          // Generate correct path
          const path0 = navGenPath(random, length, sizeX, sizeY, start);

          // Generate 3 distractor paths (different end, not start)
          const wrongPaths: number[][] = [];
          while (wrongPaths.length < 3) {
            const p = navGenPath(random, length, sizeX, sizeY, start);
            if (!navCellEq(p.end, path0.end) && !navCellEq(p.end, start)) {
              wrongPaths.push(p.directions);
            }
          }

          const rect = new PdfRect(
            origin.x + x * size.width,
            origin.y + y * size.height,
            size.width,
            size.height
          );
          const sz = (size.width - 2 * margin) / sizeX;
          const o = new PdfPoint(rect.x + margin, rect.y + margin);

          // Draw 6×6 grid cells
          for (let x1 = 0; x1 < sizeX; x1++) {
            for (let y1 = 0; y1 < sizeY; y1++) {
              const r = new PdfRect(o.x + x1 * sz, o.y + y1 * sz, sz, sz);
              const c: NavCell = { x: x1, y: y1 };
              const inner = new PdfRect(r.x + 2, r.y + 2, r.width - 4, r.height - 4);

              if (navCellEq(c, start)) {
                pg.drawLabel(item.left, { rect: inner });
              } else if (navCellEq(c, path0.end)) {
                pg.drawLabel(item.right, { rect: inner });
              } else {
                // Draw directional arrow at cells adjacent to start
                for (let dir = 0; dir < 4; dir++) {
                  const neighbor: NavCell = { x: start.x + NAV_DX[dir], y: start.y + NAV_DY[dir] };
                  if (navCellEq(c, neighbor)) {
                    switch (dir) {
                      case 0:
                        pg.drawArrowUp(inner, blueBrush);
                        break;
                      case 1:
                        pg.drawArrowRight(inner, blueBrush);
                        break;
                      case 2:
                        pg.drawArrowDown(inner, blueBrush);
                        break;
                      case 3:
                        pg.drawArrowLeft(inner, blueBrush);
                        break;
                    }
                  }
                }
              }

              pg.drawRectangle(GUtil.basePen, null, r);
            }
          }

          pg.drawRectangle(GUtil.cutPen, null, rect);

          // Shuffle all 4 paths (3 wrong + 1 correct) and list below grid
          const allPaths = [...wrongPaths, path0.directions];
          for (let i = allPaths.length - 1; i > 0; i--) {
            const j = random.next(0, i + 1);
            [allPaths[i], allPaths[j]] = [allPaths[j], allPaths[i]];
          }

          const lineH = PdfUnit.FromMillimeters(9);
          const numberWidth = PdfUnit.FromMillimeters(8);
          const symSize = PdfUnit.FromMillimeters(9);
          const symFontSize = 28;
          for (let i = 0; i < allPaths.length; i++) {
            const numStr = `${i + 1})`;
            const pathRect = new PdfRect(
              o.x,
              o.y + sizeY * sz + i * lineH,
              size.width - 2 * margin,
              lineH
            );
            pg.drawString(numStr, listFont, PdfBrush.Black, null, pathRect, ETextAlign.CenterLeft);

            for (let d = 0; d < allPaths[i].length; d++) {
              const symbols = [ESymbol.KeyUp, ESymbol.KeyRight, ESymbol.KeyDown, ESymbol.KeyLeft];
              const symRect = new PdfRect(
                o.x + numberWidth + d * symSize,
                pathRect.y,
                symSize,
                lineH
              );
              pg.drawSymbol(
                symbols[allPaths[i][d]],
                PdfBrush.Black,
                symRect,
                symFontSize,
                ETextAlign.Center
              );
            }
          }

          index++;
        }
      }
    }
  }
}
